import { startOfDay } from "date-fns";
import { getResource } from "../language/resources/getResource";
import { formatDate, toEndOfDayString, toStartOfDayString } from "../language/time";
import { LIFT_ACCESS_RULE_SCOPE_CATEGORY } from "../productAdministration/accessRules/requests/accessRuleRequestMapper";
import { normalizeToDateOnly } from "../productAdministration/accessRules/ruleEditorTimeUtils";
import {
  EditableEffectiveDateRangeAccessRule,
  LiftAccessRuleHistory,
  ValidationConfigurationUpdateRequest,
} from "./models";

// A cleared picker reaches the validators as an Invalid Date, and every comparison with it is false
const isMissingDate = (date: Date | undefined): boolean =>
  !date || isNaN(date.getTime());

/**
 * Validates updates to effective (start) date and expiration date for EntitlementTypeMetadata
 *
 * Returns an error message to surface to the user if something is wrong. If all is right, this function returns `undefined`.
 */
const validateEffectiveDate = (
  resources: Record<string, any>,
  season: string,
  effectiveDate: Date,
  dateFormat: string,
): string | undefined => {
  // No null fields can be entered in the date fields.
  if (isMissingDate(effectiveDate)) {
    return getResource(resources, "VALIDATION_CONFIG_INVALID_EFFECTIVE_DATE");
  }

  // Hard validation rule: within one year of the season (2023-2024 season = valid from 1/1/2022 - 12/31/2025).
  // This will be the validation range in the API.
  // Parse season for start/end years
  if (season) {
    const hardSeasonStart = getHardSeasonStart(season);
    if (hardSeasonStart && effectiveDate < hardSeasonStart) {
      return `${getResource(resources, "VALIDATION_CONFIG_EFFECTIVE_DATE_OUTSIDE_ONE_YEAR_OF_SEASON_START")} - ${formatDate(hardSeasonStart, dateFormat)}.`;
    }

    const hardSeasonEnd = getHardSeasonEnd(season);
    if (hardSeasonEnd && effectiveDate > hardSeasonEnd) {
      return `${getResource(resources, "VALIDATION_CONFIG_EFFECTIVE_DATE_OUTSIDE_ONE_YEAR_OF_SEASON_END")} - ${formatDate(hardSeasonEnd, dateFormat)}.`;
    }
  }

  return undefined;
};

const validateExpirationDate = (
  resources: Record<string, any>,
  season: string,
  expirationDate: Date,
  dateFormat: string,
): string | undefined => {
  if (isMissingDate(expirationDate)) {
    return getResource(resources, "VALIDATION_CONFIG_INVALID_EXPIRATION_DATE");
  }

  if (season) {
    const hardSeasonEnd = getHardSeasonEnd(season);
    if (hardSeasonEnd && expirationDate > hardSeasonEnd) {
      return `${getResource(resources, "VALIDATION_CONFIG_EXPIRATION_DATE_OUTSIDE_ONE_YEAR_OF_SEASON_END")} - ${formatDate(hardSeasonEnd, dateFormat)}.`;
    }

    const hardSeasonStart = getHardSeasonStart(season);
    if (hardSeasonStart && expirationDate < hardSeasonStart) {
      return `${getResource(resources, "VALIDATION_CONFIG_EXPIRATION_DATE_OUTSIDE_ONE_YEAR_OF_SEASON_START")} - ${formatDate(hardSeasonStart, dateFormat)}.`;
    }
  }

  return undefined;
};

/**
 * Checks for overlapping start and expire dates for metadata for an entitlement type with multiple fulfillment seasons
 *
 * Returns true if date is within the date range of the comparison EditableEffectiveDateRangeAccessRule
 */
export const effectiveDateRangeDatesOverlap = (
  field: string,
  effectiveDateRange: EditableEffectiveDateRangeAccessRule,
  ruleToCompare: EditableEffectiveDateRangeAccessRule,
): boolean => {
  const dateToCheck: Date =
    field === "effectiveDate"
      ? new Date(effectiveDateRange.effectiveDate)
      : new Date(effectiveDateRange.expirationDate);
  const compareEffectiveDate = new Date(ruleToCompare.effectiveDate);
  const compareExpirationDate = new Date(ruleToCompare.expirationDate);

  return (
    dateIsWithinDateRange(
      dateToCheck,
      compareEffectiveDate,
      compareExpirationDate,
    ) ||
    (new Date(effectiveDateRange.effectiveDate) <= compareEffectiveDate &&
      new Date(effectiveDateRange.expirationDate) >= compareExpirationDate)
  );
};

const dateIsWithinDateRange = (
  dateToCheck: Date,
  effectiveDate: Date,
  expirationDate: Date,
): boolean => {
  return dateToCheck >= effectiveDate && dateToCheck <= expirationDate;
};

// Ends load at 23:59:59 and season limits sit at midnight, so the validators compare calendar days
const toDay = (date: Date): Date => (date ? startOfDay(date) : date);

export const validateEffectiveDateRangeRulesForErrors = (
  resources: Record<string, any>,
  season: string,
  effectiveDateTime: Date,
  expirationDateTime: Date,
  propName: string,
  dateFormat: string,
): string | undefined => {
  const effectiveDate = toDay(effectiveDateTime);
  const expirationDate = toDay(expirationDateTime);
  let error: string | undefined = undefined;
  switch (propName) {
    case "effectiveDate":
      error = validateEffectiveDate(
        resources,
        season,
        effectiveDate,
        dateFormat,
      );
      break;
    case "expirationDate":
      error = validateExpirationDate(
        resources,
        season,
        expirationDate,
        dateFormat,
      );
      break;
    default:
      break;
  }

  // Start Date cannot be after End Date.
  // End Date cannot be before Start Date.
  if (effectiveDate > expirationDate) {
    error = getResource(
      resources,
      "VALIDATION_CONFIG_EXPIRES_BEFORE_EFFECTIVE_DATE",
    );
  }

  return error;
};

export const validateEffectiveDateRangeRulesForWarnings = (
  resources: Record<string, any>,
  season: string,
  effectiveDateTime: Date,
  expirationDateTime: Date,
  propName: string,
  dateFormat: string,
): string | undefined => {
  const effectiveDate = toDay(effectiveDateTime);
  const expirationDate = toDay(expirationDateTime);
  //  Soft validation rule: add a warning if the date is outside the years of the season (2023-2024 season = 1/1/2023-12/31/2024).
  //  This is the typical required range, so want to prompt the user to ensure anything outside this is actually correct.
  //  There are a few outliers that require an extended range.
  switch (propName) {
    case "effectiveDate":
      if (season) {
        const softSeasonStart = getSoftSeasonStart(season);

        if (softSeasonStart && effectiveDate < softSeasonStart) {
          return `${getResource(resources, "VALIDATION_CONFIG_EFFECTIVE_DATE_OUTSIDE_SEASON_START")} - ${formatDate(softSeasonStart, dateFormat)}.`;
        }
      }
      break;
    case "expirationDate":
      if (season) {
        const softSeasonEnd = getSoftSeasonEnd(season);
        if (softSeasonEnd && expirationDate > softSeasonEnd) {
          return `${getResource(resources, "VALIDATION_CONFIG_EXPIRATION_DATE_OUTSIDE_SEASON_END")} - ${formatDate(softSeasonEnd, dateFormat)}.`;
        }
      }
      break;
    default:
      break;
  }

  return undefined;
};

const getHardSeasonStart = (season: string) => {
  const seasonStartYearMatch = RegExp(/\d{4}/).exec(season);
  if (seasonStartYearMatch && seasonStartYearMatch.length > 0) {
    const seasonStartYear = parseInt(seasonStartYearMatch[0]);
    if (seasonStartYear) {
      return new Date(seasonStartYear - 1, 0, 1);
    }
  }
  return undefined;
};

const getHardSeasonEnd = (season: string) => {
  const seasonEndYearMatch = RegExp(/\d{4}$/).exec(season);
  if (seasonEndYearMatch && seasonEndYearMatch?.length > 0) {
    const seasonEndYear = parseInt(seasonEndYearMatch[0]);
    if (seasonEndYear) {
      return new Date(seasonEndYear + 1, 11, 31);
    }
  }
  return undefined;
};

const getSoftSeasonStart = (season: string) => {
  const seasonStartYearMatch = RegExp(/\d{4}/).exec(season);
  if (seasonStartYearMatch && seasonStartYearMatch.length > 0) {
    const seasonStartYear = parseInt(seasonStartYearMatch[0]);
    if (seasonStartYear) {
      return new Date(seasonStartYear, 0, 1);
    }
  }
  return undefined;
};

const getSoftSeasonEnd = (season: string) => {
  const seasonEndYearMatch = RegExp(/\d{4}$/).exec(season);
  if (seasonEndYearMatch && seasonEndYearMatch.length > 0) {
    const seasonEndYear = parseInt(seasonEndYearMatch[0]);
    if (seasonEndYear) {
      return new Date(seasonEndYear, 11, 31);
    }
  }
  return undefined;
};

export const getChangeHistoryForEffectiveDateRangeRule = (
  effectiveDateRangeRule: EditableEffectiveDateRangeAccessRule,
  changeHistory: LiftAccessRuleHistory[],
): LiftAccessRuleHistory | undefined => {
  return changeHistory?.find(
    (history) =>
      history.entitlementTypeId === effectiveDateRangeRule.entitlementTypeId &&
      history.resortId === effectiveDateRangeRule.resortId &&
      history.fulfillmentSeasonId ===
        effectiveDateRangeRule.fulfillmentSeasonId,
  );
};

// EffectiveDateRangeRule is Unique by entitlementTypeId, resortId and fulfillmentSeasonId (also liftAccessRuleId but that is not used for
// history lookups since the ruleId can change if a rule is deleted and recreated)
export const effectiveDateRangeRuleUniqueKey = (
  edr: EditableEffectiveDateRangeAccessRule,
) => `${edr.entitlementTypeId}_${edr.resortId}_${edr.fulfillmentSeasonId}`;

// Calendar days, not strings: ends load at 23:59:59 and picked dates arrive at 00:00:00
export const effectiveDateRangeDatesChanged = (
  original: EditableEffectiveDateRangeAccessRule,
  current: EditableEffectiveDateRangeAccessRule,
): boolean =>
  normalizeToDateOnly(original.effectiveDate) !==
    normalizeToDateOnly(current.effectiveDate) ||
  normalizeToDateOnly(original.expirationDate) !==
    normalizeToDateOnly(current.expirationDate);

export const effectiveDateRangeRuleUniqueKeyWithField = (
  edr: EditableEffectiveDateRangeAccessRule,
  field: string,
) =>
  `${edr.entitlementTypeId}_${edr.resortId}_${edr.fulfillmentSeasonId}_${field}`;

export function buildValidationConfigurationUpdateRequest(
  rules: EditableEffectiveDateRangeAccessRule[],
  resortAbbreviation: string,
  operatorId: string,
  userId: string,
): ValidationConfigurationUpdateRequest {
  return {
    operatorId: operatorId,
    userId: userId,
    requestUtcDateTime: new Date().toISOString(),
    resortAbbreviation: resortAbbreviation,
    updateHierarchyChildEntitlementTypes: true,
    updateBenefitChildEntitlementTypes: true,
    effectiveDateRangeRuleUpdates: rules.map((rule) => ({
      context: {
        EntitlementTypeId: rule.entitlementTypeId,
        FulfillmentSeasonId: rule.fulfillmentSeasonId,
        LiftAccessRuleScopeCategory: LIFT_ACCESS_RULE_SCOPE_CATEGORY.RESORT,
        LiftAccessRuleScopeEntityId: rule.resortId,
      },
      rule: {
        liftAccessRuleId: rule.liftAccessRuleId,
        // Local time, no offset: whole days, 00:00:00 to 23:59:59
        effectiveDateStart: rule.effectiveDate && toStartOfDayString(rule.effectiveDate),
        effectiveDateEnd: rule.expirationDate && toEndOfDayString(rule.expirationDate),
        effectiveTimeSlotStart: rule.effectiveTimeSlotStart,
        effectiveTimeSlotEnd: rule.effectiveTimeSlotEnd,
      },
    })),
  };
}
